/**
 * Print insight / quote strips for Monthly Consultation PDF.
 * Light warm strips match the website; darkQuote matches Kai Wen identity bars
 * (charcoal + gold italic — never red-on-navy labels).
 */

import React from "react";
import { Sparkles } from "lucide-react";
import {
  bodyFontFamily,
  border,
  cardRadius,
  cardWhite,
  charcoal,
  displayFontFamily,
  gold,
  green,
  insightDark,
  mutedGray,
} from "../pdfTheme";

/**
 * Visual variants for insight strips.
 * - darkQuote: charcoal bar, gold italic (identity lines)
 * - softGold: protect / press coaching
 * - softSuccess: exit moves / positive close
 * - softWarn: traps / cautions
 * - softCoral: general emphasis on cream
 * - executiveBar: Letter closing bar — sparkle, gold label, white body
 */
export type PdfInsightVariant =
  | "darkQuote"
  | "executiveBar"
  | "softGold"
  | "softSuccess"
  | "softWarn"
  | "softCoral";

/**
 * Props for a printable insight strip.
 */
export interface PdfInsightStripProps {
  /** Optional small uppercase label. */
  label?: string;
  /** Body text. */
  text: string;
  /** Visual treatment. Defaults to softGold. */
  variant?: PdfInsightVariant;
}

interface VariantStyle {
  background: string;
  border: string;
  labelColor: string;
  textColor: string;
  italic: boolean;
  leftAccent: string | null;
}

/**
 * Resolve colors for an insight variant.
 *
 * @param variant - Strip variant key
 * @returns Style tokens for the strip
 */
function styleForVariant(variant: PdfInsightVariant): VariantStyle {
  if (variant === "darkQuote") {
    return {
      background: charcoal,
      border: "none",
      labelColor: gold,
      textColor: "#e8d5a8",
      italic: true,
      leftAccent: null,
    };
  }
  if (variant === "softSuccess") {
    return {
      background: "#ecfdf5",
      border: "1px solid #a7f3d0",
      labelColor: green,
      textColor: charcoal,
      italic: false,
      leftAccent: green,
    };
  }
  if (variant === "softWarn") {
    return {
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      labelColor: "#c2410c",
      textColor: charcoal,
      italic: false,
      leftAccent: "#c2410c",
    };
  }
  if (variant === "softCoral") {
    return {
      background: cardWhite,
      border: `1px solid ${border}`,
      labelColor: "#e8642d",
      textColor: charcoal,
      italic: true,
      leftAccent: "#e8642d",
    };
  }
  return {
    background: `${gold}18`,
    border: `1px solid ${border}`,
    labelColor: gold,
    textColor: charcoal,
    italic: false,
    leftAccent: gold,
  };
}

/**
 * Insight strip used instead of red-on-charcoal dark callouts.
 */
export const PdfInsightStrip: React.FC<PdfInsightStripProps> = ({
  label,
  text,
  variant = "softGold",
}) => {
  const safeText = typeof text === "string" ? text.trim() : "";
  if (safeText.length === 0) {
    return null;
  }

  const safeLabel =
    typeof label === "string" && label.trim().length > 0 ? label.trim() : null;

  if (variant === "executiveBar") {
    return (
      <aside
        className="mc-pdf-dark-callout mc-pdf-letter-executive"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          background: insightDark,
          border: "none",
          borderRadius: cardRadius,
          padding: "14px 16px",
          boxSizing: "border-box",
          width: "100%",
          fontFamily: bodyFontFamily,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: `${gold}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Sparkles size={20} color={gold} strokeWidth={2} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {safeLabel !== null ? (
            <p
              style={{
                margin: 0,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: gold,
              }}
            >
              {safeLabel}
            </p>
          ) : null}
          <p
            style={{
              margin: safeLabel !== null ? "4px 0 0" : 0,
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 500,
              color: cardWhite,
              fontFamily: bodyFontFamily,
            }}
          >
            {safeText}
          </p>
        </div>
      </aside>
    );
  }

  const visual = styleForVariant(variant);

  return (
    <aside
      className="mc-pdf-dark-callout"
      style={{
        background: visual.background,
        border: visual.border,
        borderLeft:
          visual.leftAccent !== null
            ? `4px solid ${visual.leftAccent}`
            : visual.border,
        borderRadius: cardRadius,
        padding: "14px 18px",
        boxSizing: "border-box",
        width: "100%",
        fontFamily: bodyFontFamily,
      }}
    >
      {safeLabel !== null ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: visual.labelColor,
            marginBottom: 6,
          }}
        >
          {safeLabel}
        </div>
      ) : null}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          lineHeight: 1.55,
          fontWeight: 500,
          color: visual.textColor,
          fontFamily: visual.italic ? displayFontFamily : bodyFontFamily,
          fontStyle: visual.italic ? "italic" : "normal",
        }}
      >
        {variant === "darkQuote" || variant === "softCoral"
          ? `“${safeText}”`
          : safeText}
      </p>
      {variant === "darkQuote" ? (
        <span style={{ display: "none", color: mutedGray }} aria-hidden="true">
          {/* keep mutedGray referenced for theme consistency */}
        </span>
      ) : null}
    </aside>
  );
};

/**
 * @deprecated Prefer {@link PdfInsightStrip}. Kept as a thin alias so older
 * chapter imports keep compiling during the redesign.
 */
export const PdfDarkCallout: React.FC<{
  label?: string;
  text: string;
  accent?: string;
}> = ({ label, text }) => (
  <PdfInsightStrip label={label} text={text} variant="softCoral" />
);
